import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Paper,
  TableContainer,
  Tabs,
  Tab,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import PlaceIcon from "@mui/icons-material/Place";
import CategoryIcon from "@mui/icons-material/Category";
import api from "../../utils/api";
import WorkOrderBillingConfig from "./WorkOrderBillingConfig";
import ModuleStatuses from "./ModuleStatuses";
import WorkOrderFormBuilder from "./WorkOrderFormBuilder";
import WOAdvancedSettings from "./WOAdvancedSettings";
import WorkOrderManual from "./WorkOrderManual";
import { ThemeContext } from "../../utils/ThemeContext";

const WorkOrderManager = () => {
  const { isDark } = useContext(ThemeContext);
  const [activeTab, setActiveTab] = useState(0);

  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingSubtype, setEditingSubtype] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({ type: "", id: null });
  const [subtypeDialog, setSubtypeDialog] = useState(false);
  const [locationDialog, setLocationDialog] = useState(false);
  const [subtypeForm, setSubtypeForm] = useState({
    id: null,
    type_id: null,
    subtype_name: "",
  });
  const [locationForm, setLocationForm] = useState({
    id: null,
    type_id: null,
    location_name: "",
    description: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchTypes();
    fetchSubtypes();
    fetchLocations();
  }, []);

  const fetchTypes = async () => {
    const res = await api.get(`/workorder-types`);
    setTypes(res.data);
  };

  const fetchSubtypes = async () => {
    const res = await api.get(`/workorder-subtypes`);
    setSubtypes(res.data);
  };

  const fetchLocations = async () => {
    const res = await api.get(`/workorder-locations`);
    setLocations(res.data);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleTabChange = (_, value) => {
    setActiveTab(value);
  };

  const handleInputChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const openAddDialog = (type = null) => {
    if (type) {
      setFormData(type);
      setIsEditing(true);
    } else {
      setFormData({ type: "", id: null });
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setFormData({ type: "", id: null });
    setIsEditing(false);
  };

  const handleFormSubmit = async () => {
    try {
      if (isEditing) {
        await api.put(`/workorder-types/${formData.id}`, formData);
        showSnackbar("Work order type updated");
      } else {
        await api.post(`/workorder-types`, formData);
        showSnackbar("Work order type added");
      }
      fetchTypes();
      closeDialog();
    } catch (err) {
      showSnackbar("Error saving type", "error");
    }
  };

  const confirmDeleteType = async (id) => {
    const relatedSubtypes = subtypes.filter((s) => s.type_id === id);
    const relatedLocations = locations.filter((l) => l.type_id === id);
    const typeLabel = types.find((t) => t.id === id)?.type;

    const warning = `Deleting "${typeLabel}" will also remove ${relatedSubtypes.length} subtypes and ${relatedLocations.length} locations. Continue?`;

    if (window.confirm(warning)) {
      try {
        await api.delete(`/workorder-types/${id}`);
        showSnackbar("Type, subtypes, and locations deleted");
        fetchTypes();
        fetchSubtypes();
        fetchLocations();
      } catch (err) {
        showSnackbar("Error deleting type", "error");
      }
    }
  };

  const openSubtypeDialog = (type_id) => {
    setSubtypeForm({ id: null, type_id, subtype_name: "" });
    setEditingSubtype(null);
    setSubtypeDialog(true);
  };

  const editSubtype = (subtype) => {
    setSubtypeForm(subtype);
    setEditingSubtype(subtype.id);
    setSubtypeDialog(true);
  };

  const handleSubtypeChange = (e) => {
    setSubtypeForm({ ...subtypeForm, [e.target.name]: e.target.value });
  };

  const handleSubtypeSubmit = async () => {
    try {
      if (editingSubtype) {
        await api.put(`/workorder-subtypes/${editingSubtype}`, subtypeForm);
        showSnackbar("Subtype updated");
      } else {
        await api.post(`/workorder-subtypes`, subtypeForm);
        showSnackbar("Subtype added");
      }
      fetchSubtypes();
      setSubtypeDialog(false);
    } catch (err) {
      showSnackbar("Error saving subtype", "error");
    }
  };

  const deleteSubtype = async (id) => {
    if (!window.confirm("Delete this subtype?")) return;
    try {
      await api.delete(`/workorder-subtypes/${id}`);
      showSnackbar("Subtype deleted");
      fetchSubtypes();
    } catch (err) {
      showSnackbar("Error deleting subtype", "error");
    }
  };

  const openLocationDialog = (type_id) => {
    setLocationForm({ id: null, type_id, location_name: "", description: "" });
    setEditingLocation(null);
    setLocationDialog(true);
  };

  const editLocation = (location) => {
    setLocationForm(location);
    setEditingLocation(location.id);
    setLocationDialog(true);
  };

  const handleLocationChange = (e) => {
    setLocationForm({ ...locationForm, [e.target.name]: e.target.value });
  };

  const handleLocationSubmit = async () => {
    try {
      if (editingLocation) {
        await api.put(`/workorder-locations/${editingLocation}`, locationForm);
        showSnackbar("Location updated");
      } else {
        await api.post(`/workorder-locations`, locationForm);
        showSnackbar("Location added");
      }
      fetchLocations();
      setLocationDialog(false);
    } catch (err) {
      showSnackbar("Error saving location", "error");
    }
  };

  const deleteLocation = async (id) => {
    if (!window.confirm("Delete this location?")) return;
    try {
      await api.delete(`/workorder-locations/${id}`);
      showSnackbar("Location deleted");
      fetchLocations();
    } catch (err) {
      showSnackbar("Error deleting location", "error");
    }
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    backgroundColor: isDark ? "#1a2c46" : "rgba(255,255,255,0.96)",
    backdropFilter: "blur(10px)",
    boxShadow: isDark ? "0 20px 60px rgba(0,0,0,0.55)" : "0 20px 60px rgba(0,0,0,0.25)",
    border: isDark ? "1px solid rgba(255,255,255,0.08)" : "none",
    overflow: "hidden",
  };

  const fieldSx = {
    mt: 1.25,
    "& .MuiOutlinedInput-root": {
      backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
  };

  const miniActionButtonSx = {
    mt: 1,
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    color: "#2f63ad",
  };

  const inlineItemSx = {
    px: 1.25,
    py: 1,
    borderRadius: "10px",
    backgroundColor: "rgba(47,99,173,0.04)",
    border: "1px solid rgba(47,99,173,0.08)",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 1,
    mb: 1,
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: "auto",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              minHeight: 40,
              color: "#5f6b7a",
              mr: 1,
              px: { xs: 1.5, md: 2 },
              py: 1,
              whiteSpace: "nowrap",
              minWidth: "fit-content",
            },
            "& .Mui-selected": {
              color: "#2f63ad !important",
              backgroundColor: "rgba(47,99,173,0.08)",
            },
            "& .MuiTabs-indicator": {
              display: "none",
            },
          }}
        >
          <Tab label="Structure" />
          <Tab label="Billing Setup" />
          <Tab label="Statuses" />
          <Tab label="Form Builder" />
          <Tab label="Advanced" />
          <Tab label="Manual" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              mb: 3,
            }}
          >
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "#223142",
                  mb: 0.5,
                }}
              >
                Work Order Types
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "#6b778c",
                }}
              >
                Manage work order types, related subtypes, and location options.
              </Typography>
            </Box>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openAddDialog()}
              sx={primaryButtonSx}
            >
              Add Work Order Type
            </Button>
          </Box>

          <Paper
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid rgba(47,99,173,0.08)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
              overflow: "hidden",
              backgroundColor: "rgba(255,255,255,0.96)",
            }}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: "32%" }}>
                      Subtypes
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, width: "32%" }}>
                      Locations
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {types.length > 0 ? (
                    types.map((type) => {
                      const typeSubtypes = subtypes.filter(
                        (s) => s.type_id === type.id
                      );
                      const typeLocations = locations.filter(
                        (l) => l.type_id === type.id
                      );

                      return (
                        <TableRow key={type.id}>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {type.type}
                          </TableCell>

                          <TableCell sx={{ verticalAlign: "top" }}>
                            {typeSubtypes.length ? (
                              typeSubtypes.map((s) => (
                                <Box key={s.id} sx={inlineItemSx}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <CategoryIcon
                                      sx={{
                                        fontSize: 18,
                                        color: "#2f63ad",
                                        mt: "2px",
                                      }}
                                    />
                                    <Typography variant="body2">
                                      {s.subtype_name}
                                    </Typography>
                                  </Box>

                                  <Box sx={{ flexShrink: 0 }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => editSubtype(s)}
                                    >
                                      <Edit fontSize="inherit" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => deleteSubtype(s.id)}
                                    >
                                      <Delete fontSize="inherit" />
                                    </IconButton>
                                  </Box>
                                </Box>
                              ))
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{ color: "#6b778c", mb: 1 }}
                              >
                                No subtypes added.
                              </Typography>
                            )}

                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => openSubtypeDialog(type.id)}
                              sx={miniActionButtonSx}
                            >
                              Add Subtype
                            </Button>
                          </TableCell>

                          <TableCell sx={{ verticalAlign: "top" }}>
                            {typeLocations.length ? (
                              typeLocations.map((loc) => (
                                <Box key={loc.id} sx={inlineItemSx}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                      gap: 1,
                                    }}
                                  >
                                    <PlaceIcon
                                      sx={{
                                        fontSize: 18,
                                        color: "#2f63ad",
                                        mt: "2px",
                                      }}
                                    />
                                    <Box>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 600 }}
                                      >
                                        {loc.location_name}
                                      </Typography>
                                      {loc.description ? (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: "#6b778c",
                                            display: "block",
                                            mt: 0.25,
                                          }}
                                        >
                                          {loc.description}
                                        </Typography>
                                      ) : null}
                                    </Box>
                                  </Box>

                                  <Box sx={{ flexShrink: 0 }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => editLocation(loc)}
                                    >
                                      <Edit fontSize="inherit" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => deleteLocation(loc.id)}
                                    >
                                      <Delete fontSize="inherit" />
                                    </IconButton>
                                  </Box>
                                </Box>
                              ))
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{ color: "#6b778c", mb: 1 }}
                              >
                                No locations added.
                              </Typography>
                            )}

                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => openLocationDialog(type.id)}
                              sx={miniActionButtonSx}
                            >
                              Add Location
                            </Button>
                          </TableCell>

                          <TableCell align="right" sx={{ verticalAlign: "top" }}>
                            <IconButton onClick={() => openAddDialog(type)}>
                              <Edit />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() => confirmDeleteType(type.id)}
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ color: "text.secondary" }}>
                        No work order types found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Dialog
            open={openDialog}
            onClose={closeDialog}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: dialogPaperSx }}
          >
            <DialogTitle
              sx={{
                textAlign: "center",
                fontWeight: 700,
                fontSize: "1.35rem",
                pt: 3,
                pb: 1,
                color: "#1f1f1f",
              }}
            >
              {isEditing ? "Edit Work Order Type" : "Add Work Order Type"}
            </DialogTitle>

            <DialogContent sx={{ px: 3, pb: 1 }}>
              <Typography
                sx={{
                  mt: 0.5,
                  mb: 1,
                  textAlign: "center",
                  color: "#666",
                  fontSize: "0.95rem",
                }}
              >
                Enter the work order type name below.
              </Typography>

              <TextField
                fullWidth
                label="Type"
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                sx={fieldSx}
              />
            </DialogContent>

            <DialogActions
              sx={{
                px: 3,
                pb: 3,
                pt: 1.5,
                justifyContent: "center",
                gap: 1.25,
              }}
            >
              <Button
                onClick={closeDialog}
                variant="outlined"
                sx={secondaryButtonSx}
              >
                Cancel
              </Button>
              <Button
                onClick={handleFormSubmit}
                variant="contained"
                sx={primaryButtonSx}
              >
                {isEditing ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={subtypeDialog}
            onClose={() => setSubtypeDialog(false)}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: dialogPaperSx }}
          >
            <DialogTitle
              sx={{
                textAlign: "center",
                fontWeight: 700,
                fontSize: "1.35rem",
                pt: 3,
                pb: 1,
                color: "#1f1f1f",
              }}
            >
              {editingSubtype ? "Edit Subtype" : "Add Subtype"}
            </DialogTitle>

            <DialogContent sx={{ px: 3, pb: 1 }}>
              <Typography
                sx={{
                  mt: 0.5,
                  mb: 1,
                  textAlign: "center",
                  color: "#666",
                  fontSize: "0.95rem",
                }}
              >
                Enter the subtype name for this work order type.
              </Typography>

              <TextField
                fullWidth
                label="Subtype Name"
                name="subtype_name"
                value={subtypeForm.subtype_name}
                onChange={handleSubtypeChange}
                sx={fieldSx}
              />
            </DialogContent>

            <DialogActions
              sx={{
                px: 3,
                pb: 3,
                pt: 1.5,
                justifyContent: "center",
                gap: 1.25,
              }}
            >
              <Button
                onClick={() => setSubtypeDialog(false)}
                variant="outlined"
                sx={secondaryButtonSx}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubtypeSubmit}
                variant="contained"
                sx={primaryButtonSx}
              >
                {editingSubtype ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={locationDialog}
            onClose={() => setLocationDialog(false)}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: dialogPaperSx }}
          >
            <DialogTitle
              sx={{
                textAlign: "center",
                fontWeight: 700,
                fontSize: "1.35rem",
                pt: 3,
                pb: 1,
                color: "#1f1f1f",
              }}
            >
              {editingLocation ? "Edit Location" : "Add Location"}
            </DialogTitle>

            <DialogContent sx={{ px: 3, pb: 1 }}>
              <Typography
                sx={{
                  mt: 0.5,
                  mb: 1,
                  textAlign: "center",
                  color: "#666",
                  fontSize: "0.95rem",
                }}
              >
                Enter the location details below.
              </Typography>

              <TextField
                fullWidth
                label="Location Name"
                name="location_name"
                value={locationForm.location_name}
                onChange={handleLocationChange}
                sx={fieldSx}
              />

              <TextField
                fullWidth
                label="Description"
                name="description"
                value={locationForm.description}
                onChange={handleLocationChange}
                multiline
                rows={3}
                sx={fieldSx}
              />
            </DialogContent>

            <DialogActions
              sx={{
                px: 3,
                pb: 3,
                pt: 1.5,
                justifyContent: "center",
                gap: 1.25,
              }}
            >
              <Button
                onClick={() => setLocationDialog(false)}
                variant="outlined"
                sx={secondaryButtonSx}
              >
                Cancel
              </Button>
              <Button
                onClick={handleLocationSubmit}
                variant="contained"
                sx={primaryButtonSx}
              >
                {editingLocation ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={3000}
            onClose={closeSnackbar}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert severity={snackbar.severity} onClose={closeSnackbar}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </>
      )}

      {activeTab === 1 && <WorkOrderBillingConfig />}
      {activeTab === 2 && <ModuleStatuses moduleId={1} moduleName="Work Orders" />}
      {activeTab === 3 && <WorkOrderFormBuilder />}
      {activeTab === 4 && <WOAdvancedSettings />}
      {activeTab === 5 && <WorkOrderManual />}
    </Box>
  );
};

export default WorkOrderManager;